import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "../../../i18n/LanguageContext";
import { SITE } from "../../../content/site";
import { routePath } from "../../../lib/routes";
import { track, EVENTS } from "../../../lib/analytics";

const ease = [0.16, 1, 0.3, 1];
const CLIPS = [1, 2, 3, 4, 5].map((n) => ({ webm: `/videos/hero-${n}.webm`, mp4: `/videos/hero-${n}.mp4` }));

function HeroVideo() {
  const [active, setActive] = useState(0);
  const [clips, setClips] = useState([0, 1 % CLIPS.length]);
  const [ready, setReady] = useState(false);
  const refA = useRef(null);
  const refB = useRef(null);
  const refs = [refA, refB];
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (reduced) return;
    const v = refs[active].current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p) p.then(() => setReady(true)).catch(() => {});
  }, [active, reduced]); // eslint-disable-line react-hooks/exhaustive-deps

  if (reduced) return null;

  const handleEnded = (layer) => {
    if (layer !== active) return;
    const next = 1 - layer;
    const nextClip = (clips[layer] + 1) % CLIPS.length;
    setClips((c) => (next === 0 ? [nextClip, c[1]] : [c[0], nextClip]));
    setActive(next);
  };

  return (
    <>
      {[0, 1].map((layer) => (
        <video
          key={`${layer}-${clips[layer]}`}
          ref={refs[layer]}
          muted
          playsInline
          autoPlay={layer === active}
          preload="auto"
          onEnded={() => handleEnded(layer)}
          data-testid={`home-hero-video-${layer}`}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            ready && active === layer ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src={CLIPS[clips[layer]].webm} type="video/webm" />
          <source src={CLIPS[clips[layer]].mp4} type="video/mp4" />
        </video>
      ))}
    </>
  );
}

export default function HomeHero() {
  const { lang } = useI18n();
  const c = (SITE[lang].homeStory || SITE.en.homeStory).hero;
  const alt = (SITE[lang].events || SITE.en.events).alt;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const words = c.architecture.split("·").map((w) => w.trim());

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-[108vh] min-h-[780px] overflow-hidden bg-white md:h-[118vh]">
      <motion.div style={{ y }} className="absolute inset-0 h-[112%] w-full">
        <img
          src="/events/hero.jpg" alt={alt.hero} width="1920" height="1245" fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <HeroVideo />
      </motion.div>
      <div className="absolute inset-0 bg-black/25" />
      {/* localized legibility scrim behind the copy */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(115%_85%_at_0%_60%,rgba(5,5,5,0.62)_0%,rgba(5,5,5,0.28)_40%,transparent_66%)]" />
      {/* long progressive fade to white */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-b from-transparent via-white/70 to-white md:h-[34%]" />
      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-[36vh] md:px-12 md:pb-[44vh]">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease, delay: 0.15 }}
          data-testid="home-hero-architecture"
          className="mb-7 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-medium uppercase tracking-[0.4em] text-white/75 md:text-xs"
        >
          {words.map((w, i) => (
            <React.Fragment key={w}>
              {i > 0 && <span className="h-1 w-1 rounded-full bg-[#2aa08c]" />}
              <span>{w}</span>
            </React.Fragment>
          ))}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease, delay: 0.3 }}
          className="font-display max-w-5xl text-4xl font-light leading-[0.98] tracking-tightest text-white [text-shadow:0_2px_28px_rgba(0,0,0,0.45)] sm:text-5xl lg:text-[5.2rem]"
        >
          {c.headline}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease, delay: 0.65 }}
          className="mt-7 max-w-2xl text-base font-light leading-relaxed text-white/85 [text-shadow:0_1px_18px_rgba(0,0,0,0.5)] md:text-lg"
        >
          {c.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease, delay: 0.9 }}
          className="mt-11 flex flex-col gap-4 sm:flex-row"
        >
          <Link to={routePath("events", lang)} data-testid="home-hero-cta-events"
            onClick={() => track(EVENTS.CTA_CLICK, { cta: "home_hero_events" })}
            className="ep-btn ep-btn-light">
            {c.ctaEvents} <ArrowRight size={18} />
          </Link>
          <Link to={routePath("book", lang)} data-testid="home-hero-cta-book"
            onClick={() => track(EVENTS.CTA_CLICK, { cta: "home_hero_book" })}
            className="ep-btn ep-btn-glass">
            {c.ctaBook}
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
